import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import ChannelLabel, { channelInfo } from '@op/components/ChannelLabel';
import { getOpinionFilterCond } from '@op/services';
import { sortArrayAuxiliaryFunc, uniqueArray } from '@op/utils';
import './index.scss';
import { useDispatch, useIntl, useSelector } from 'umi';
import { channelSortList } from '@op/constants/channel';

export type ChannelSourceType = {
  source: { [key: string]: string[] };
  channel: string[];
};

// ['comments', 'followers', 'likes', 'plays', 'share', 'unlike', 'views'];
const metricToSource: any = {
  impressions: ['followers'],
  // publications: 'all',
  creators: 'all',
  views: ['views', 'plays'],
  likes: ['likes'],
  comments: ['comments'],
  shares: ['share'],
  engagement: ['likes', 'unlike', 'comments', 'share'],
  engagement_rate: ['followers', 'likes', 'unlike', 'comments', 'share'],
};

export function useMetricsChannel({ gameInfo }: { gameInfo: any }) {
  const [loading, setLoading] = useState(false);
  const { formatMessage } = useIntl();
  const [channelSource, setChannelSource] = useState<{
    [key: string]: string[];
  }>({});
  const [channelList, setChannelList] = useState<string[]>([]);
  const dispatch = useDispatch();
  const prevEditionIdRef = useRef<string | null>(null);
  const { storedChannelList, storedChannelSource, storedEditionId } =
    useSelector((state: any) => {
      const { opinionFilterCond } = state;
      return {
        storedChannelList: opinionFilterCond?.channelList || [],
        storedChannelSource: opinionFilterCond?.channelSource || {},
        storedEditionId:
          opinionFilterCond?.gameInfo?.edition_unified_id || null,
      };
    });

  useEffect(() => {
    const currentEditionId = gameInfo.edition_unified_id;
    if (
      currentEditionId === storedEditionId &&
      storedChannelList.length > 0 &&
      Object.keys(storedChannelSource).length > 0
    ) {
      setChannelSource((prev) =>
        prev !== storedChannelSource ? storedChannelSource : prev,
      );
      setChannelList((prev) =>
        prev !== storedChannelList ? storedChannelList : prev,
      );
      return;
    }

    if (!currentEditionId || currentEditionId === prevEditionIdRef.current) {
      return;
    }

    prevEditionIdRef.current = currentEditionId;

    setLoading(true);
    getOpinionFilterCond({
      ...gameInfo,
      request_type: ['source_norm', 'channel'],
      un_request_type: ['topic_trans'],
    })
      .then((data: any) => {
        const list = data?.source_norm || [];
        const sourceMap: any = {};
        // 总量 1,2,3
        const channelList: Array<string> = data?.channel?.social || [];

        // 分组数据 4
        list.map((item: any) => {
          const value = item.source;

          // 交集:去掉不在总量内渠道
          if (!channelList.includes(value)) return;

          Object.keys(item).forEach((key: any) => {
            if (key === 'source') return;

            if (!sourceMap[key]) sourceMap[key] = [];
            const flag = item[key] === 1;
            if (flag) sourceMap[key].push(value);
          });
        });

        channelList.sort(sortArrayAuxiliaryFunc(channelSortList));
        dispatch({
          type: 'opinionFilterCond/save',
          payload: {
            channelSource: sourceMap,
            channelList,
            gameInfo,
          },
        });
        setChannelSource(sourceMap);
        setChannelList(channelList);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [gameInfo, storedEditionId]);

  const MergeChannelIconElement = useCallback(
    ({ metric }: { metric: string }) => {
      const [channelText, setChannelText] = useState('');
      const handleMouseEnter = (text: string, extText: string) => {
        setChannelText(extText + text);
      };

      const handleMouseLeave = () => {
        setChannelText('');
      };

      function getChannelElements(list: Array<string>, className?: string) {
        return list.map((item: any) => {
          const { flag, name } = channelInfo(item);
          if (flag) return null;
          return (
            <div
              key={item}
              style={{ display: 'inline-block' }}
              onMouseEnter={() =>
                handleMouseEnter(
                  name,
                  className ? formatMessage({ id: 'Excluding' }) + ' ' : '',
                )
              }
              onMouseLeave={handleMouseLeave}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <ChannelLabel
                iconStyle={{ width: 16, height: 16, marginRight: 6 }}
                showText={false}
                className={className}
                channel_name={item}
              />
            </div>
          );
        });
      }

      let list = metricToSource?.[metric];

      if (!list || channelList.length === 0) return null;
      let renderList: string[] = [];
      if (list === 'all') {
        renderList = channelList;
      } else {
        list.map((key: string) => {
          const list = channelSource[key];
          renderList = renderList.concat(list);
        });
      }

      renderList = uniqueArray(renderList);
      renderList.sort(sortArrayAuxiliaryFunc(channelSortList));
      // 亮色和暗色   总渠道 - 亮色渠道 = 暗色渠道
      const darkList = channelList.filter((item) => !renderList.includes(item));

      const renderLightIcon = getChannelElements(renderList);

      const darkLightIcon = getChannelElements(
        darkList,
        'dbo-dark-channel-icon',
      );

      return (
        <div className="dbo-channel-data-from">
          <p className="dbo-channel-data-from-title">
            {formatMessage({ id: 'Data Source:' })} {channelText}
          </p>
          <div className="dbo-channel-icon-list">
            {renderLightIcon}
            {darkLightIcon}
          </div>
        </div>
      );
    },
    [channelSource, channelList, formatMessage],
  );

  return MergeChannelIconElement;
}
